'use client';

import CheckboxGroup from '../../../atoms/CheckboxGroup/CheckboxGroup';
import { getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { CheckboxGroupField } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type CheckboxGroupControlProps = {
  field: CheckboxGroupField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
};

/**
 * Form control for checkbox group fields
 * @component
 * @param {CheckboxGroupControlProps} props - Component props
 * @returns {JSX.Element} CheckboxGroup control component
 */
const CheckboxGroupControl = ({
  field,
  theme,
  direction,
  register,
  setValue,
  watch,
  errors,
}: CheckboxGroupControlProps) => {
  const { disabled: formDisabled } = useFormContext();
  const isDisabled = field.disabled || formDisabled;

  return (
    <CheckboxGroup
      key={`form_field_${field.name}`}
      theme={theme}
      name={field.name}
      label={field.label}
      helperText={field.helperText}
      tooltipText={field.tooltipText}
      options={field.options}
      selectedValues={watch(field.name) || []}
      layout={field.layout}
      dataTestId={`checkbox-group-${field.name}`}
      gaComponent=""
      register={() =>
        register(field.name, {
          required: field.required ? field.validationMessages?.required : false,
        })
      }
      handleChange={(values) => setValue(field.name, values, { shouldValidate: true })}
      validationResponse={getValidationResponse({ field: field.name, errors })}
      direction={direction}
      required={field.required}
      isDisabled={isDisabled}
      badge={field.badge}
    />
  );
};

export default CheckboxGroupControl;
